import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../shared/services/notification.service';

import { SdsListQrCode } from '../models/sds-public.model';

/**
 * Minimal shape of the angularx-qrcode component instance we rely on.
 * A template ref on the <qrcode> element resolves to the component instance,
 * which exposes its rendered wrapper through `qrcElement`.
 */
interface QrCodeComponentRef {
  qrcElement: ElementRef<HTMLElement>;
}

/**
 * Manage the QR code that opens a business' public chemical list: generate it, look at it,
 * download it as a PNG, print it for the wall, and revoke it.
 *
 * The QR image is rendered locally by angularx-qrcode. It deliberately does not go through a
 * QR-image web service, the way the per-document share QR in the list still does: that would
 * hand the public URL of a business' whole chemical inventory to a third party on every render.
 */
@Component({
  selector: 'sds-list-qr',
  templateUrl: 'sds-list-qr.component.html',
  styleUrls: ['sds-list-qr.component.css'],
  standalone: false
})
export class SdsListQrComponent {
  @Input() visible = false;
  @Input() qrCode: SdsListQrCode | null = null;
  @Input() isLoading = false;
  /** False for roles that may look at the code but not change it. */
  @Input() canManage = true;

  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emits the chosen expiry as yyyy-MM-dd, or null for a code that never expires. */
  @Output() createQrCodeEvent = new EventEmitter<string | null>();
  @Output() revokeQrCodeEvent = new EventEmitter<void>();

  @ViewChild('qrCodeElement') qrCodeElement?: QrCodeComponentRef;

  /** Confirmation step before revoking, so a printed code is never killed by one stray click. */
  isRevoking = false;
  /** Confirmation step before regenerating, which also invalidates the printed code. */
  isRegenerating = false;

  minDate: Date = new Date();
  expirationDate: Date | null = null;

  constructor(
    private translate: TranslateService,
    private notificationService: NotificationService
  ) {}

  onHide(): void {
    this.visible = false;
    this.isRevoking = false;
    this.isRegenerating = false;
    this.visibleChange.emit(false);
  }

  /**
   * Emit the create event with the selected expiry as a local yyyy-MM-dd string (what the
   * backend's DateOnly expects). Formatting in local time avoids the off-by-one day a UTC
   * conversion would cause.
   */
  createQrCode(): void {
    const expiresOn = this.expirationDate
      ? formatDate(this.expirationDate, 'yyyy-MM-dd', 'en-US')
      : null;

    this.isRegenerating = false;
    this.createQrCodeEvent.emit(expiresOn);
  }

  /** Show the regenerate confirmation: the old URL dies with the new code. */
  onRegenerate(): void {
    this.isRegenerating = true;
  }

  onCancelRegenerate(): void {
    this.isRegenerating = false;
  }

  /** Show the revoke confirmation. */
  onRevokeQrCode(): void {
    this.isRevoking = true;
  }

  onCancelRevoke(): void {
    this.isRevoking = false;
  }

  onConfirmRevoke(): void {
    this.revokeQrCodeEvent.emit();
    this.isRevoking = false;
  }

  /** Human-readable expiry, or a localized "never expires". */
  displayExpirationDate(): string {
    if (!this.qrCode?.ExpiresOn) {
      return this.translate.instant('NO_EXPIRATION_DATE');
    }

    return formatDate(this.qrCode.ExpiresOn, 'dd.MM.yyyy', 'en-US');
  }

  copyLink(): void {
    if (!this.qrCode?.QrCodeUrl) {
      return;
    }

    navigator.clipboard?.writeText(this.qrCode.QrCodeUrl);
    this.notificationService.success('SDS_SHARE_LINK_COPIED');
  }

  openLink(): void {
    if (!this.qrCode?.QrCodeUrl) {
      return;
    }

    window.open(this.qrCode.QrCodeUrl, '_blank', 'noopener');
  }

  /** Download the rendered QR code as a PNG. */
  downloadQrCode(): void {
    const dataUrl = this.readQrDataUrl();
    if (!dataUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${this.translate.instant('SDS_PUBLIC_LIST_QR_FILE_NAME')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Print the code as a single A4 sheet, sized to be scannable from a metre or so away and
   * captioned so whoever walks past knows what they are pointing a phone at.
   */
  printQrCode(): void {
    const dataUrl = this.readQrDataUrl();
    if (!dataUrl) {
      return;
    }

    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';

    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument || printWindow?.document;

    if (!printWindow || !printDocument) {
      document.body.removeChild(printFrame);
      return;
    }

    const title = this.escapeHtml(this.translate.instant('SDS_PUBLIC_LIST_QR_TITLE'));
    const caption = this.escapeHtml(this.translate.instant('SDS_PUBLIC_LIST_QR_PRINT_CAPTION'));

    printWindow.onafterprint = () => {
      setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 500);
    };

    printDocument.open();
    printDocument.write(`
<!doctype html>
<html>
  <head>
    <title>${title}</title>
    <style>
      @page { size: A4; margin: 0; }
      html, body { width: 100%; height: 100%; margin: 0; }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
        text-align: center;
      }
      h1 { font-size: 34px; margin: 0 0 8px; }
      p { font-size: 20px; margin: 0 0 28px; color: #333; }
      img { width: 135mm; height: 135mm; }
    </style>
  </head>
  <body>
    <h1>${title}</h1>
    <p>${caption}</p>
    <img src="${dataUrl}" alt="${title}" />
  </body>
</html>`);
    printDocument.close();

    // Give the image a tick to decode before the print dialog snapshots the document.
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  }

  /** Pull the rendered QR out of the angularx-qrcode element, whether it drew a canvas or an img. */
  private readQrDataUrl(): string | null {
    const element = this.qrCodeElement?.qrcElement?.nativeElement;
    if (!element) {
      return null;
    }

    const canvas = element.querySelector('canvas');
    if (canvas) {
      return canvas.toDataURL('image/png');
    }

    return element.querySelector('img')?.src ?? null;
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
